import React, { Fragment, useCallback, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { useUser } from './context';
import { ACCOUNT_BASE_PATH } from './pages/accountPaths';

const accountLinkClassName = (active: boolean) =>
  `flex min-h-[48px] items-center px-4 py-3 font-sans text-sm font-semibold text-ink ${
    active ? 'bg-skysoft/40' : ''
  }`;

const AccountMenuLink = ({ active }: { active: boolean }) => (
  <Link to={ACCOUNT_BASE_PATH} className={accountLinkClassName(active)}>
    Account
  </Link>
);

const LogoutMenuButton = ({
  active,
  loggingOut,
  onLogout,
}: {
  active: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) => (
  <button
    type="button"
    disabled={loggingOut}
    className={`flex min-h-[48px] w-full items-center gap-2 px-4 py-3 text-left font-sans text-sm font-semibold ${
      active ? 'bg-ink/5 text-ink' : 'text-ink/75'
    } disabled:cursor-not-allowed disabled:opacity-50`}
    onClick={onLogout}
  >
    <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0 text-ink/50" aria-hidden />
    {loggingOut ? 'Signing out…' : 'Log out'}
  </button>
);

const UserAccountMenu = () => {
  const { user } = useUser();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      const res = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Logout failed (${res.status})`);
      }
      if (window.location.pathname === '/') {
        window.location.reload();
      } else {
        window.location.assign('/');
      }
    } catch (e) {
      setLoggingOut(false);
      console.error(e);
    }
  }, []);

  if (!user) return null;

  const displayName =
    user.firstName?.trim() || user.email.split('@')[0] || 'Account';

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        type="button"
        className="inline-flex items-center gap-2 rounded-full bg-paper/90 py-1.5 pl-2 pr-3 font-sans text-sm font-semibold text-ink shadow-sm ring-2 ring-ink/10 transition hover:bg-skysoft/30 hover:ring-ink/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/35"
        aria-label={`Account menu for ${displayName}`}
        aria-haspopup="menu"
      >
        <UserCircleIcon className="h-8 w-8 shrink-0 text-doghouse" aria-hidden />
        <span className="max-w-[10rem] truncate">{displayName}</span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-ink/50" aria-hidden />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className="absolute right-0 top-full z-50 mt-0 w-56 origin-top-right rounded-2xl border-2 border-ink/10 bg-white py-1 shadow-[4px_8px_0_rgba(44,44,44,0.1)] ring-1 ring-black/5 focus:outline-none"
        >
          <Menu.Item>
            {({ active }) => <AccountMenuLink active={active} />}
          </Menu.Item>
          <div className="my-1 border-t border-ink/10" role="presentation" />
          <Menu.Item>
            {({ active }) => (
              <LogoutMenuButton
                active={active}
                loggingOut={loggingOut}
                onLogout={() => void handleLogout()}
              />
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default UserAccountMenu;
